'use client'
import styles from './Hero.module.sass';
import Image, {getImageProps} from "next/image";
import Button from "@/components/Button/Button";
import cn from "classnames";
import gsap from "gsap";
import {useGameContext} from "@/context/GameContext";
import {useGSAP} from "@gsap/react";
import {useEffect, useRef} from "react";
import SplitText from "gsap/SplitText";

const logoProps = {
	alt:           'Logo Affectio',
	width:         576,
	height:        506,
	loading:       'eager',
	fetchPriority: 'high',
}
const {props: {srcSet: logoMobileSrcSet}} = getImageProps({...logoProps, src: '/Global/logo_full_mobile.png'})
const {props: logoDesktopProps} = getImageProps({...logoProps, src: '/Global/logo_full.png'})

const Hero = () => {
	const src = "/sound/button_click.mp3"

	const {isPlaying, setIsPlaying, isMobile} = useGameContext()

	const {heroTimelineRef, playHeroAnimation} = useGameContext();

	const presentedByUpperRef = useRef();
	const presentedByLowerRef = useRef();


	useEffect(() => {
		if(playHeroAnimation) {
			heroTimelineRef.current.play()
		}
	}, [playHeroAnimation]);

	//  Appartion of the hero elements
	useGSAP(() => {
		new SplitText(presentedByUpperRef.current, {types: "chars", charsClass: 'presented_by_upper_text'});
		new SplitText(presentedByLowerRef.current, {types: "chars", charsClass: 'presented_by_lower_text'});

		const elasticEasing = 'elastic.out(1.3,0.4)'

		heroTimelineRef.current = gsap.timeline({paused: true})

		heroTimelineRef.current
		// Small pause before the animation
		.fromTo('.dark_star',  {opacity: 0, scale: 0}, {opacity: 1, scale: 1, duration: 1, delay: .5, ease: elasticEasing})
		.fromTo('.light_star',  {opacity: 0, scale: 0}, {opacity: 1, scale: 1, duration: 1, ease: elasticEasing}, '<+=.2')
		.call(() => {
			gsap.fromTo('.burger_btn', {
				opacity: 0,
			}, {opacity: 1, duration: 1, delay: .5})
		})
		.fromTo('.presented_by_upper_text', {
			visibility: 'hidden',
		}, {
			visibility: 'visible',
			duration: .01,
			stagger: .033,
		}, '>')
		.addLabel('presented_by_upper_text_shown')

		heroTimelineRef.current.fromTo('.presented_by_lower_text', {
			visibility: 'hidden',
		}, {
			visibility: 'visible',
			duration: .01,
			stagger: .033,
		}, 'presented_by_upper_text_shown')
		heroTimelineRef.current.fromTo('.big_logo__container', {scale: 0},{
			scale: 1,
			duration: 1,
			ease: elasticEasing
		}, '>')
		heroTimelineRef.current.fromTo('.big_logo__container', {opacity: 0},{
			opacity: 1,
			duration: .5,
		}, '<')
		heroTimelineRef.current.fromTo('.subtitle', {scale: 0},{
			scale: 1,
			duration: 1,
			ease: elasticEasing
		}, '<+=.33')
		heroTimelineRef.current.fromTo('.subtitle', {opacity: 0},{
			opacity: 1,
			duration: .5,
		}, '<')
		heroTimelineRef.current.fromTo('.play_button', {scale: 0},{
			scale: 1,
			duration: 1,
			ease: elasticEasing
		}, '<+=.33')
		heroTimelineRef.current.fromTo('.play_button', {opacity: 0},{
			opacity: 1,
			duration: .5,
		}, '<')

	}, [])

	// Play button click handler
	const handlePlayClick = () => {
		setIsPlaying(true);
		if(isMobile) return
		window.audios[src].play()
	}

	useEffect(() => {
		if(isMobile) return
		window.audios = window.audios || {}

		if(!window.audios[src]) {
			// Create the new Audio
			window.audios[src] = new Audio(src);
			window.audios[src].volume = 1;
		}

	}, [isMobile]);

	useGSAP(() => {
		if (isPlaying) {
			// Hide the hero
			const playTl = gsap.timeline();
			playTl.to('.hero', {duration: 1, opacity: 0, y: '-100%', ease: "power2.out"})
				.call(() => {
					//Remove the Overflow on the body
					document.body.classList.remove('no_scroll')
				})
		}
	}, [isPlaying]);

	return (
	<section className={cn(styles.hero, 'hero')} data-lenis-prevent>
		<div className={cn(styles.background, {[styles.stopAnimation]: isPlaying})}>
			{/*Stars SVG*/}
			<svg className={cn(styles.stars, 'dark_star')}
			     width="2564"
			     height="2596"
			     viewBox="0 0 2564 2596"
			     xmlns="http://www.w3.org/2000/svg">
				<path d="M1510.27 1739.17L1500.83 2595.4L1167.84 1806.67L610.551 2456.61L862.206 1638.15L17.827 1777.69L736.378 1312.46L0 876.319L849.23 982.014L565.412 174.254L1147.96 801.414L1449.5 0L1492.78 855.17L2238.59 435.092L1722.36 1118.13L2563.46 1275.95L1729.26 1467.25L2272.1 2129.12L1510.27 1739.17Z"
				      fill="#002C3D"/>
			</svg>
			<svg className={cn(styles.stars, 'light_star')}
			     width="2564"
			     height="2596"
			     viewBox="0 0 2564 2596"
			     xmlns="http://www.w3.org/2000/svg">
				<path d="M1535.96 1415.63L1871.16 1816.15L1403.97 1582.75L1403.35 2105L1195.43 1625.9L859.285 2025.53L1007.93 1524.9L493.54 1614.91L929.196 1327L477.253 1065.28L996.072 1124.81L818.043 633.815L1177.27 1012.93L1356.45 522.407L1387.99 1043.71L1840.55 783.181L1529.65 1202.75L2043.83 1294.12L1535.96 1415.63Z"
				      fill="#003549"/>
			</svg>
		</div>

		<div className={styles.center}>
			{/*Presented by*/}
			<h2 className={styles.presented_by}>
				<span ref={presentedByUpperRef}>JONATHAN SIGNORET</span><span ref={presentedByLowerRef}>PRÉSENTE</span>
			</h2>

			{/*Logo*/}
			<div className={cn(styles.big_logo__container, 'big_logo__container')}>
				<picture>
					<source media={'(max-width: 767px)'} srcSet={logoMobileSrcSet}/>
					<img {...logoDesktopProps} className={styles.big_logo}/>
				</picture>
				{!isMobile && <Image className={styles.big_logo__sparkles}
				                     src={'/images/Game/Glow/sparkles.gif'}
				                     alt={"Sparkles"}
				                     width={64}
				                     height={64}
				                     unoptimized={true}/>}
			</div>

			{/*Subtitle*/}
			<h3 className={cn(styles.subtitle, 'subtitle')}>LE DROIT DES AFFAIRES AUTREMENT</h3>

			{/*	Play Button*/}
			<Button className={cn(styles.play_button, 'custom_pointer', 'play_button')}
			        height={ isMobile ? '4rem' : '6rem'}
			        borderSize={'2.5px'}
			        gradientPercentage={'85%'}
			        onClick={handlePlayClick}
			><span className={styles.play}>Play</span></Button>

		</div>
	</section>
	  );
}
export default Hero;